import type { CustomerRow, CustomerSort, SortOrder } from '@/api/customers'

/** `sort` → the row field it orders by (README *Sort keys*). */
export const SORT_FIELDS: Readonly<Record<CustomerSort, keyof CustomerRow>> = {
  last_purchase_at: 'lastPurchaseAt',
  first_purchase_at: 'firstPurchaseAt',
  registered_at: 'registeredAt',
  orders: 'orders',
  tickets: 'tickets',
  range_orders: 'rangeOrders',
  range_tickets: 'rangeTickets',
  lifetime_revenue: 'lifetimeRevenueMinor',
  refunds: 'refundsMinor',
  aov: 'aovMinor',
  range_revenue: 'rangeRevenueMinor',
}

/**
 * Customers in the API's order for `sort`: the largest or latest first for `desc`, a
 * null always last in either direction, ties by `tie` (by default the order given, which
 * a stable sort keeps). The demo sorts its list by it; the page sorts a search's few
 * found customers by it, rather than searching (and auditing) again for a new order.
 */
export function sortCustomers<T>(
  items: readonly T[],
  rowOf: (item: T) => CustomerRow,
  sort: CustomerSort,
  order: SortOrder,
  tie: (a: T, b: T) => number = () => 0,
): T[] {
  const field = SORT_FIELDS[sort]
  const direction = order === 'desc' ? -1 : 1
  return [...items].sort((a, b) => {
    const left = rowOf(a)[field]
    const right = rowOf(b)[field]
    if (left === null && right === null) return tie(a, b)
    if (left === null) return 1
    if (right === null) return -1
    const byValue = typeof left === 'number' && typeof right === 'number' ? left - right : String(left).localeCompare(String(right))
    return direction * byValue || tie(a, b)
  })
}
